import React, { useState, useEffect } from 'react';
import { addRecord, fetchMaintenanceTypes } from '../services/api';

interface MaintenanceFormProps {
  carId: number;
  onSuccess: () => void;
  onCancel: () => void;
}

const MaintenanceForm: React.FC<MaintenanceFormProps> = ({ carId, onSuccess, onCancel }) => {
  const [types, setTypes] = useState<any[]>([]);
  const [formData, setFormData] = useState({
    type_id: '',
    service_date: new Date().toISOString().split('T')[0],
    service_mileage: '',
    cost: '',
    notes: '',
    detailed_info: {} as any
  });

  const getDetailFields = () => {
    const type = types.find(t => t.id === parseInt(formData.type_id));
    if (!type) return null;

    if (type.name.includes('Aceite')) {
      return (
        <div className="detail-fields" style={{ background: 'rgba(56, 189, 248, 0.05)', padding: '1rem', borderRadius: '8px', marginBottom: '1rem' }}>
          <label style={{ color: 'var(--accent-color)', fontWeight: 'bold' }}>Detalles del Aceite</label>
          <input 
            type="text" placeholder="Ej: Sintético 5W-30" 
            onChange={(e) => setFormData({...formData, detailed_info: {...formData.detailed_info, 'Tipo de Aceite': e.target.value}})}
          />
          <input 
            type="text" placeholder="Marca (Ej: Castrol)" style={{ marginTop: '0.5rem' }}
            onChange={(e) => setFormData({...formData, detailed_info: {...formData.detailed_info, 'Marca': e.target.value}})}
          />
        </div>
      );
    }
    if (type.name.includes('Neumáticos')) {
      const position = type.name.includes('Delanteros') ? 'Delanteros' : type.name.includes('Traseros') ? 'Traseros' : 'Todos';
      return (
        <div className="detail-fields" style={{ background: 'rgba(56, 189, 248, 0.05)', padding: '1rem', borderRadius: '8px', marginBottom: '1rem' }}>
          <label style={{ color: 'var(--accent-color)', fontWeight: 'bold' }}>Detalles de Neumáticos ({position})</label>
          <input 
            type="text" placeholder="Marca (Ej: Michelin, Pirelli)" 
            onChange={(e) => setFormData({...formData, detailed_info: {...formData.detailed_info, 'Marca': e.target.value, 'Posición': position}})}
          />
          <input 
            type="text" placeholder="Modelo/Medida (Ej: Pilot Sport 225/45 R17)" style={{ marginTop: '0.5rem' }}
            onChange={(e) => setFormData({...formData, detailed_info: {...formData.detailed_info, 'Modelo/Medida': e.target.value}})}
          />
        </div>
      );
    }
    return null;
  };
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });

  useEffect(() => {
    const loadTypes = async () => {
      try {
        const data = await fetchMaintenanceTypes();
        setTypes(data);
      } catch (error) {
        console.error("Error loading types", error);
      }
    };
    loadTypes();
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setMessage({ text: '', type: '' });

    try {
      const result = await addRecord({
        ...formData,
        car_id: carId,
        service_mileage: parseInt(formData.service_mileage),
        cost: parseFloat(formData.cost) || 0
      });

      if (result.error) {
        setMessage({ text: result.error, type: 'danger' });
      } else {
        setMessage({ text: '¡Mantenimiento registrado con éxito!', type: 'success' });
        setTimeout(() => onSuccess(), 500); // Reduced from 1500 to 500
      }
    } catch (error) {
      setMessage({ text: 'Error de conexión con el servidor.', type: 'danger' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="card maintenance-form-container">
      <h2>Registrar Mantenimiento</h2>
      <p style={{ color: 'var(--text-secondary)', marginBottom: '1.5rem' }}>
        Ingresá los detalles del servicio realizado.
      </p>

      {message.text && (
        <div className={`status-badge status-${message.type}`} style={{ marginBottom: '1rem', display: 'block', textAlign: 'center' }}>
          {message.text}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="input-group">
          <label>Tipo de Servicio</label>
          <select 
            name="type_id" 
            value={formData.type_id} 
            onChange={handleChange} 
            required
            style={{ width: '100%', padding: '0.75rem', borderRadius: '8px', backgroundColor: '#0f172a', color: 'white', border: '1px solid var(--border)' }}
          >
            <option value="">Seleccioná un tipo...</option>
            {types.map(t => (
              <option key={t.id} value={t.id}>{t.name}</option>
            ))}
          </select>
        </div>

        {getDetailFields()}

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
          <div className="input-group">
            <label>Fecha</label>
            <input 
              type="date" 
              name="service_date" 
              value={formData.service_date} 
              onChange={handleChange} 
              required 
            />
          </div>
          <div className="input-group">
            <label>Kilometraje</label>
            <input 
              type="number" 
              name="service_mileage" 
              value={formData.service_mileage} 
              onChange={handleChange} 
              placeholder="Ej: 55000" 
              required 
            />
          </div>
        </div>

        <div className="input-group">
          <label>Costo (Opcional)</label>
          <input 
            type="number" 
            step="0.01" 
            name="cost" 
            value={formData.cost} 
            onChange={handleChange} 
            placeholder="0.00" 
          />
        </div>

        <div className="input-group">
          <label>Notas / Observaciones</label>
          <textarea 
            name="notes" 
            value={formData.notes} 
            onChange={handleChange} 
            rows={3}
            placeholder="¿Algún detalle especial?"
            style={{ width: '100%', padding: '0.75rem', borderRadius: '8px', backgroundColor: '#0f172a', color: 'white', border: '1px solid var(--border)', resize: 'vertical' }}
          ></textarea>
        </div>

        <div className="car-actions" style={{ marginTop: '1rem' }}>
          <button type="button" className="btn btn-outline" onClick={onCancel} disabled={isSubmitting}>
            Cancelar
          </button>
          <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
            {isSubmitting ? 'Guardando...' : 'Guardar Registro'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default MaintenanceForm;
